import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import type { QuizSetDetailsRes } from '@/api/gen/_model/quizSetDetailsRes';
import { showQuizSavedLinkNudge } from '@/shared/features/classroom/components';
import {
  QUIZ_SAVED_LINK_NUDGE_COPY_VALUE,
  QUIZ_SAVED_LINK_NUDGE_QUERY_KEY as QUIZ_SAVED_NUDGE_QUERY_KEY,
} from '@/shared/features/classroom/consts/classLinkNudge';
import type { QuizSavedNudgeFrom } from '@/shared/features/classroom/consts/events.quiz-classroom-link-nudge';
import { useQuizSavedLinkNudgePolicy } from '@/shared/features/classroom/hooks/useQuizSavedLinkNudgePolicy';
import { isContentLinkedToClassroom } from '@/shared/features/classroom/utils';

/**
 * 새 퀴즈 저장 직후 상세에 도착하면 클래스 연결 권유 모달을 띄운다.
 * 빌드 저장·복사가 `useQuizSavedLinkNudgePolicy` 조건일 때만 이 쿼리 키를 넘기고, 여기서는 띄운 뒤
 * **이 키만** 지운다. isDuplicate 등 다른 쿼리는 다른 소비자가 읽는다.
 * 넘어온 주소가 공유돼 다른 사람이 열 수 있으므로 같은 정책을 한 번 더 보고, 소유자인지도 본다.
 */
export function useQuizSavedLinkNudgeEffect({
  quizSet,
  isOwner,
}: {
  quizSet?: QuizSetDetailsRes;
  isOwner: boolean;
}) {
  const router = useRouter();
  const { isReady, shouldNudge } = useQuizSavedLinkNudgePolicy();
  const canLinkToClassroom = isOwner && shouldNudge;
  // 처리한 퀴즈 id. 상세 → 상세(복사 직후 사본으로) 이동은 페이지를 다시 만들지 않으므로
  // boolean 이 아니라 id 로 기억해야 다음 퀴즈에서 다시 판단한다.
  const handledQuizId = useRef<string | null>(null);
  const nudgeQuery = router.query[QUIZ_SAVED_NUDGE_QUERY_KEY];
  const requested =
    nudgeQuery === 'true' || nudgeQuery === QUIZ_SAVED_LINK_NUDGE_COPY_VALUE;
  // 계측용 출처. 복사는 쿼리 값으로, 복제는 빌드가 함께 넘기는 `isDuplicate` 로 가린다.
  const from: QuizSavedNudgeFrom =
    nudgeQuery === QUIZ_SAVED_LINK_NUDGE_COPY_VALUE
      ? 'copy'
      : router.query.isDuplicate === 'true'
      ? 'duplicate'
      : 'build';
  const routeQuizId = router.query.id;

  useEffect(() => {
    // 정책 값이 확정되기 전(마운트·사용자 초기화 전)에는 판단하지 않는다. 한 번 판단하면 키를 지워서다.
    if (!requested || !quizSet || !isReady) return;
    // 이동 직후에는 아직 이전 퀴즈(복사 원본) 데이터가 남아 있다. 그걸로 판단하면 원본이
    // 남의 퀴즈라 소유자 조건에서 빠지고, 사본 데이터가 와도 다시 보지 않아 모달이 안 뜬다.
    if (quizSet.id !== routeQuizId || handledQuizId.current === quizSet.id) {
      return;
    }
    handledQuizId.current = quizSet.id;

    const { [QUIZ_SAVED_NUDGE_QUERY_KEY]: _, ...restQuery } = router.query;
    void router.replace(
      { pathname: router.pathname, query: restQuery },
      undefined,
      { shallow: true },
    );

    if (!canLinkToClassroom) return;
    void isContentLinkedToClassroom(quizSet.id, 'QUIZSET')
      .then(isLinked => {
        if (isLinked) return;
        showQuizSavedLinkNudge({
          quizSetId: quizSet.id,
          mapImageUrl: quizSet.templateImageUrl ?? '',
          from,
        });
      })
      // 연결 여부를 못 읽으면 권유는 건너뛴다. 저장은 이미 끝났다.
      .catch(() => undefined);
  }, [
    canLinkToClassroom,
    from,
    isReady,
    quizSet,
    requested,
    routeQuizId,
    router,
  ]);
}
